import React from 'react';
import { Button, Icon } from 'antd';
import styled from 'styled-components';
import { CHANGELOG } from '~/constants/changelog.constants';
import Player from './Player';
import { getSubdomain } from '~/helpers/getSubdomain';
import { AccountStorage } from '~/storages/account';
import * as IndexedDB from 'idb-keyval';

var Container = styled.div`
  /* 680px cortava o vídeo: um 16:9 legível pede mais largura, e o texto do
     changelog continua confortável porque as linhas quebram bem antes. */
  max-width: 780px;
`;

/**
 * Cada release é um nó da linha do tempo.
 *
 * A data vive FORA da linha, à esquerda, e o traço liga uma release à
 * seguinte. É a data que responde a pergunta com que se abre esta tela — "o
 * que saiu depois da última vez que eu olhei" — e num empilhamento de blocos
 * iguais essa informação se perde.
 */
var VersionBlock = styled.div`
  display: flex;
  gap: 16px;
  align-items: stretch;
`;

var Eixo = styled.div`
  width: 13px;
  flex-shrink: 0;
  position: relative;
`;

var Marcador = styled.span`
  position: absolute;
  top: 7px;
  left: 0;
  width: 13px;
  height: 13px;
  border-radius: 50%;
  background: ${function(p) { return p.atual ? '#4B2183' : '#fff'; }};
  border: 2px solid ${function(p) { return p.atual ? '#4B2183' : '#d9d9d9'; }};
  z-index: 1;
`;

var Traco = styled.span`
  position: absolute;
  top: 26px;
  left: 5.5px;
  width: 2px;
  /* na última release o traço vira um degradê curto: sem isso o marcador fica
     solto e o conjunto não se lê como linha do tempo */
  bottom: ${function(p) { return p.ultimo ? 'auto' : '-40px'; }};
  height: ${function(p) { return p.ultimo ? '48px' : 'auto'; }};
  background: ${function(p) {
    return p.ultimo
      ? 'linear-gradient(to bottom, #eceaf3, rgba(236,234,243,0))'
      : '#eceaf3';
  }};
`;

var DataColuna = styled.div`
  width: 58px;
  flex-shrink: 0;
  text-align: right;
  padding-top: 2px;
`;

var DataDia = styled.div`
  font-size: 21px;
  font-weight: 600;
  color: #262626;
  line-height: 1;
`;

var DataResto = styled.div`
  font-size: 12px;
  color: #8c8c8c;
  margin-top: 3px;
`;

var Conteudo = styled.div`
  flex: 1;
  min-width: 0;
  padding-bottom: 40px;
`;

var VersionHeader = styled.div`
  margin-bottom: 24px;
`;

/* O que a leva entregou, em uma linha. É o que a pessoa lê primeiro. */
var VersionTitle = styled.h2`
  font-size: 20px;
  font-weight: 700;
  line-height: 1.3;
  color: #262626;
  margin: 0 0 6px;
  max-width: 48ch;
`;

/* O número serve para amarrar o vídeo e o aviso, não para explicar a leva a
   quem abre a tela — por isso sai da posição de destaque. */
var VersionMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

var VersionNumber = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #8c8c8c;
`;

var VersionDate = styled.span`
  font-size: 14px;
  color: #8c8c8c;
`;

var VersionBadge = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: #1890ff;
  background: #e6f7ff;
  padding: 3px 10px;
  border-radius: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

var VideoBlock = styled.div`
  margin: 0 0 26px;
`;

// Mais de um vídeo na versão: cards lado a lado, menores. Em tela estreita
// (ou no painel lateral) caem para uma coluna sozinhos.
var VideoGrade = styled.div`
  margin: 0 0 26px;
`;

var VideoGradeTopo = styled.div`
  font-size: 11.5px;
  font-weight: 600;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: #8c8c8c;
  margin: 0 0 10px;
`;

var VideoGradeLista = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 16px;
  align-items: start;
`;

var VideoCard = styled.div`
  background: #fafafa;
  border: 1px solid #f0f0f0;
  border-radius: 14px;
  padding: 12px;
  transition: border-color .15s, box-shadow .15s;

  &[data-ampliado='1'] { grid-column: 1 / -1; }

  &:hover { border-color: #e6d6fb; box-shadow: 0 6px 18px -12px rgba(75, 33, 131, .35); }
`;

// Altura fixa para dois títulos de tamanhos diferentes não desalinharem os
// players lado a lado: até duas linhas de título mais a linha da duração.
var VideoCardTopo = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  margin: 2px 2px 10px;
  min-height: 58px;
`;

var VideoTitulo = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #262626;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

var VideoMeta = styled.div`
  font-size: 12px;
  color: #8c8c8c;
  margin-top: 2px;
`;

var IconeBtn = styled.button`
  flex: none;
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  width: 30px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #595959;
  cursor: pointer;

  &:hover { border-color: #b37feb; color: #4B2183; }
`;

var TrechosToggle = styled.button`
  background: none;
  border: 0;
  padding: 0;
  margin-top: 10px;
  font-size: 12.5px;
  font-family: inherit;
  color: #722ed1;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 5px;
`;

var TrechoLista = styled.div`
  margin-top: 6px;
  display: flex;
  flex-direction: column;
`;

var TrechoLinha = styled.button`
  background: none;
  border: 0;
  border-radius: 6px;
  padding: 5px 6px;
  font-size: 12.5px;
  font-family: inherit;
  color: #595959;
  text-align: left;
  cursor: pointer;
  display: flex;
  gap: 10px;

  &:hover { background: #f3e9ff; color: #262626; }
`;

var VideoLegenda = styled.div`
  font-size: 12.5px;
  color: #8c8c8c;
  margin-top: 10px;
  display: flex;
  align-items: center;
  gap: 7px;
  flex-wrap: wrap;
`;

var TrechoBtn = styled.button`
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 7px;
  padding: 4px 10px;
  font-size: 12px;
  font-family: inherit;
  color: #595959;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;

  &:hover { border-color: #b37feb; color: #262626; }
`;

var CategoryBlock = styled.div`
  margin-bottom: 28px;

  &:last-child {
    margin-bottom: 0;
  }
`;

var CategoryTitle = styled.div`
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 14px;
  color: ${function(props) { return props.color || '#8c8c8c'; }};
`;

var ChangesList = styled.div``;

var ChangeRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid #f5f5f5;

  &:last-child {
    border-bottom: none;
  }
`;

var ChangeDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${function(props) { return props.color || '#d9d9d9'; }};
  margin-top: 6px;
  flex-shrink: 0;
`;

var ChangeText = styled.div`
  flex: 1;
`;

var ChangeTitle = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #262626;
`;

var ChangeDesc = styled.span`
  font-size: 14px;
  color: #8c8c8c;
`;

var Divider = styled.div`
  height: 1px;
  background: #e8e8e8;
  margin: 32px 0;
`;

var FooterBlock = styled.div`
  background: #fafafa;
  border-radius: 8px;
  padding: 16px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

var FooterText = styled.div`
  font-size: 13px;
  color: #595959;
`;

var CATEGORY_CONFIG = {
  feature: { label: 'Novidades', color: '#1890ff' },
  improvement: { label: 'Melhorias', color: '#52c41a' },
  fix: { label: 'Correções', color: '#faad14' },
};

/**
 * Vídeos do manifesto agrupados por versão. Uma versão pode ter mais de um
 * (uma leva com duas novidades grandes); ficam na ordem do manifesto. Vídeo
 * sem `versao` ainda não foi amarrado a nenhuma release e não aparece.
 */
export function agruparPorVersao(videos) {
  var porVersao = {};
  (videos || []).forEach(function(v) {
    if (!v || !v.versao) return;
    (porVersao[v.versao] = porVersao[v.versao] || []).push(v);
  });
  return porVersao;
}

/** O player de uma versão com um vídeo só, largo, com os atalhos de trecho. */
function VideoDaVersao(props) {
  var v = props.video, mmss = props.mmss;
  var ref = React.useRef(null);
  return React.createElement(VideoBlock, null,
    React.createElement(Player, propsDoPlayer(v, ref, false)),
    React.createElement(VideoLegenda, null,
      React.createElement('span', null, 'Vídeo de ' + (v.duracao || '')),
      (v.capitulos || []).map(function(c, i) {
        return React.createElement(TrechoBtn, {
          key: i,
          onClick: function() { ref.current && ref.current.irPara(c.t); },
        },
          React.createElement('span', { style: { color: '#bfbfbf', fontVariantNumeric: 'tabular-nums' } }, mmss(c.t)),
          c.rotulo
        );
      })
    )
  );
}

function propsDoPlayer(v, ref, compacto) {
  return {
    ref: ref, src: v.src, poster: v.poster, capitulos: v.capitulos, compacto: compacto,
    comecarEm: (function() {
      try {
        var n = parseInt(window.localStorage.getItem('novidades:' + v.src), 10);
        return isFinite(n) && n > 3 ? n : 0;
      } catch (e) { return 0; }
    })(),
    onTempo: function(seg) {
      try { window.localStorage.setItem('novidades:' + v.src, String(Math.floor(seg))); } catch (e) {}
    },
  };
}

/**
 * Um card da grade de vídeos (versão com mais de um): título, duração,
 * player menor e os trechos recolhidos. "Ampliar" faz o card ocupar a linha
 * inteira, e aí ele volta a ter o tamanho do vídeo único.
 */
function VideoCardDaVersao(props) {
  var v = props.video, mmss = props.mmss, ampliado = props.ampliado;
  var ref = React.useRef(null);
  var tRef = React.useState(false); var verTrechos = tRef[0], setVerTrechos = tRef[1];
  var capitulos = (v.capitulos || []).filter(function(c) { return c.t > 0 && !/^(abertura|fecho)$/i.test(c.rotulo); });

  return React.createElement(VideoCard, { 'data-ampliado': ampliado ? '1' : '0' },
    React.createElement(VideoCardTopo, null,
      React.createElement('div', null,
        React.createElement(VideoTitulo, { title: v.titulo }, v.titulo || 'Vídeo'),
        React.createElement(VideoMeta, null,
          [v.duracao, capitulos.length ? capitulos.length + (capitulos.length === 1 ? ' trecho' : ' trechos') : null].filter(Boolean).join(' · ')
        )
      ),
      React.createElement(IconeBtn, {
        type: 'button',
        title: ampliado ? 'Reduzir' : 'Ampliar',
        'aria-label': (ampliado ? 'Reduzir' : 'Ampliar') + ' o vídeo ' + (v.titulo || ''),
        onClick: props.onAmpliar,
      }, React.createElement(Icon, { type: ampliado ? 'fullscreen-exit' : 'fullscreen' }))
    ),
    React.createElement(Player, propsDoPlayer(v, ref, !ampliado)),
    capitulos.length > 0 && React.createElement(TrechosToggle, {
      type: 'button',
      'aria-expanded': verTrechos,
      onClick: function() { setVerTrechos(!verTrechos); },
    }, React.createElement(Icon, { type: verTrechos ? 'up' : 'down', style: { fontSize: 10 } }), verTrechos ? 'Ocultar trechos' : 'Ver trechos'),
    verTrechos && React.createElement(TrechoLista, null,
      capitulos.map(function(c, i) {
        return React.createElement(TrechoLinha, {
          key: i, type: 'button',
          onClick: function() { ref.current && ref.current.irPara(c.t); },
        },
          React.createElement('span', { style: { color: '#bfbfbf', fontVariantNumeric: 'tabular-nums', minWidth: 30 } }, mmss(c.t)),
          c.rotulo
        );
      })
    )
  );
}

/** Vários vídeos na mesma versão: grade de cards, um deles pode ser ampliado. */
function GradeDeVideos(props) {
  var aRef = React.useState(null); var ampliado = aRef[0], setAmpliado = aRef[1];
  var lista = props.lista;
  return React.createElement(VideoGrade, null,
    React.createElement(VideoGradeTopo, null, lista.length + ' vídeos nesta versão'),
    React.createElement(VideoGradeLista, null,
      lista.map(function(v) {
        return React.createElement(VideoCardDaVersao, {
          key: v.src, video: v, mmss: props.mmss,
          ampliado: ampliado === v.src,
          onAmpliar: function() { setAmpliado(ampliado === v.src ? null : v.src); },
        });
      })
    )
  );
}

function VersionLog() {
  var isLoadingState = React.useState(false);
  var isLoading = isLoadingState[0];
  var setIsLoading = isLoadingState[1];

  var resetLocalStorage = function() {
    localStorage.clear();
    IndexedDB.clear();
    window.location.reload();
  };

  var handleClearCache = async function() {
    setIsLoading(true);
    try {
      var response = await fetch(process.env.REACT_APP_API + '/auth/clear-cache', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant': getSubdomain(),
          'Authorization': 'Bearer ' + AccountStorage.get().token,
        },
      });

      if (response.ok) {
        resetLocalStorage();
      } else {
        setIsLoading(false);
      }
    } catch (error) {
      setIsLoading(false);
    }
  };

  /**
   * Vídeos de novidades, buscados do manifesto no R2 e indexados por versão.
   *
   * O vídeo entra DENTRO do bloco da release que ele descreve. Uma lista
   * separada de vídeos seria o mesmo conteúdo contado duas vezes, e obrigaria
   * o leitor a cruzar as duas para saber o que assistir.
   *
   * Falha em silêncio: sem rede, ou com o manifesto fora do ar, o changelog em
   * texto continua inteiro. O vídeo é complemento, não requisito.
   */
  var vRef = React.useState({}); var videos = vRef[0], setVideos = vRef[1];
  React.useEffect(function() {
    var vivo = true;
    fetch('https://videos.contlabs.com.br/novidades/manifesto.json?t=' + Date.now())
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(d) {
        if (!vivo || !d || !d.videos) return;
        setVideos(agruparPorVersao(d.videos));
      })
      .catch(function() {});
    return function() { vivo = false; };
  }, []);

  function mmss(seg) {
    var m = Math.floor(seg / 60), r = Math.floor(seg % 60);
    return m + ':' + (r < 10 ? '0' : '') + r;
  }

  function renderVideo(versao) {
    var lista = videos[versao];
    if (!lista || !lista.length) return null;
    if (lista.length === 1) {
      return React.createElement(VideoDaVersao, { key: 'video-' + versao, video: lista[0], mmss: mmss });
    }
    return React.createElement(GradeDeVideos, { key: 'videos-' + versao, lista: lista, mmss: mmss });
  }

  function parseDescription(description) {
    var colonIndex = description.indexOf(':');
    if (colonIndex > 0 && colonIndex < 60) {
      return {
        title: description.substring(0, colonIndex).trim(),
        details: description.substring(colonIndex + 1).trim()
      };
    }
    return { title: description, details: null };
  }

  /** Dia de um lado, mês e ano de outro — o eixo empilha os dois. */
  function partesDaData(dateStr) {
    // 'YYYY-MM-DD' vira Date em UTC e volta um dia atrás em fuso negativo;
    // por isso a data é montada campo a campo, no fuso local.
    var p = String(dateStr).split('-');
    var d = new Date(Number(p[0]), Number(p[1]) - 1, Number(p[2]));
    var meses = ['jan','fev','mar','abr','mai','jun','jul','ago','set','out','nov','dez'];
    return {
      dia: String(d.getDate()).padStart(2, '0'),
      resto: meses[d.getMonth()] + ' ' + d.getFullYear(),
    };
  }

  function renderChange(change, index, color) {
    var parsed = parseDescription(change.description);

    return React.createElement(ChangeRow, { key: index },
      React.createElement(ChangeDot, { color: color }),
      React.createElement(ChangeText, null,
        React.createElement(ChangeTitle, null, parsed.title),
        parsed.details && React.createElement(ChangeDesc, null, ' — ' + parsed.details)
      )
    );
  }

  function renderCategory(changes, type) {
    var config = CATEGORY_CONFIG[type];
    var filtered = changes.filter(function(c) { return c.type === type; });

    if (filtered.length === 0) return null;

    return React.createElement(CategoryBlock, { key: type },
      React.createElement(CategoryTitle, { color: config.color }, config.label),
      React.createElement(ChangesList, null,
        filtered.map(function(change, index) {
          return renderChange(change, index, config.color);
        })
      )
    );
  }

  function renderVersion(release, index, total) {
    var isLatest = index === 0;
    var d = partesDaData(release.date);

    return React.createElement(VersionBlock, { key: release.version },
      React.createElement(DataColuna, null,
        React.createElement(DataDia, null, d.dia),
        React.createElement(DataResto, null, d.resto)
      ),
      React.createElement(Eixo, null,
        React.createElement(Marcador, { atual: isLatest }),
        React.createElement(Traco, { ultimo: index === total - 1 })
      ),
      React.createElement(Conteudo, null,
        React.createElement(VersionHeader, null,
          release.titulo
            ? React.createElement(VersionTitle, null, release.titulo)
            : null,
          React.createElement(VersionMeta, null,
            React.createElement(VersionNumber, null, release.version),
            isLatest && React.createElement(VersionBadge, null, 'Atual')
          )
        ),
        renderVideo(release.version),
        renderCategory(release.changes, 'feature'),
        renderCategory(release.changes, 'improvement'),
        renderCategory(release.changes, 'fix')
      )
    );
  }

  return React.createElement(Container, null,
    CHANGELOG.map(function(release, index) {
      return renderVersion(release, index, CHANGELOG.length);
    }),
    React.createElement(Divider, null),
    React.createElement(FooterBlock, null,
      React.createElement(FooterText, null, 'Problemas? Limpe o cache para corrigir erros de carregamento.'),
      React.createElement(Button, {
        size: 'small',
        loading: isLoading,
        onClick: handleClearCache,
      }, 'Limpar Cache')
    )
  );
}

export default VersionLog;
